
import { Link, useNavigate } from "react-router-dom";
import { ThemeToggle } from "@/components/theme/ThemeToggle";
import { useEffect, useState } from "react";
import { User, ShieldCheck, Calendar, Settings, Home, Building2 } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/auth-provider";
import { OrganizationSwitcher } from "@/components/organization/OrganizationSwitcher";
import { useOrganizationBranding } from "@/hooks/useOrganizationBranding";

const Header = () => {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const { roles, isLoading: rolesLoading } = useUserRoles();
  const { organizationName, organizationLogo, logoAltText } = useOrganizationBranding();
  const isAdmin = roles.isAdmin;
  const isSuperAdmin = roles.isPlatformAdmin;
  const isMobile = useIsMobile();
  const location = useLocation();
  const {
    user
  } = useAuth();

  // Move all hooks to the top before any conditional returns
  // Log current location for debugging
  useEffect(() => {
    console.log('[HEADER] Current location:', location.pathname);
  }, [location.pathname]);
  
  // Handle profile button click with React Router navigation
  const handleProfileClick = () => {
    console.log('[HEADER] Navigating to profile page');
    navigate('/profile');
  };

  useEffect(() => {
    // Only fetch profile if user is authenticated
    if (user) {
      const getProfile = async () => {
        const {
          data: {
            session
          }
        } = await supabase.auth.getSession();
        if (!session) return;
        const {
          data: profile
        } = await supabase.from('profiles').select('phone_number').eq('id', session.user.id).single();
        if (profile) {
          setPhoneNumber(profile.phone_number);
        }
      };
      getProfile();
    }
  }, [user]);

  // If no authenticated user, don't render the header
  if (!user) return null;

  // Bottom mobile app bar component
  const MobileAppBar = () => {
    return <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm shadow-lg border-t z-50">
        <div className="flex justify-around items-center px-2 py-2 h-18">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/app')} 
            className={`flex flex-col items-center justify-center gap-1 h-14 min-h-[44px] px-3 rounded-lg transition-colors ${
              location.pathname === '/app' 
                ? 'text-primary bg-primary/10' 
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <Home className="h-5 w-5" />
            <span className="text-[10px] font-medium">Home</span>
          </Button>
          
          <Button 
            variant="ghost" 
            onClick={() => navigate('/bookings')} 
            className={`flex flex-col items-center justify-center gap-1 h-14 min-h-[44px] px-3 rounded-lg transition-colors ${
              location.pathname === '/bookings' 
                ? 'text-primary bg-primary/10' 
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <Calendar className="h-5 w-5" />
            <span className="text-[10px] font-medium">Bookings</span>
          </Button>
          
          {isAdmin && (
            <Button 
              variant="ghost" 
              onClick={() => navigate('/admin')} 
              className={`flex flex-col items-center justify-center gap-1 h-14 min-h-[44px] px-3 rounded-lg transition-colors ${
                location.pathname === '/admin' 
                  ? 'text-primary bg-primary/10' 
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Settings className="h-5 w-5" />
              <span className="text-[10px] font-medium">Admin</span>
            </Button>
          )}
          
          {(roles.isOrgAdmin || roles.isPlatformAdmin) && (
            <Button 
              variant="ghost" 
              onClick={() => {
                console.log('[HEADER] Org Admin button clicked:', {
                  currentPath: location.pathname,
                  roles: {
                    isOrgAdmin: roles.isOrgAdmin,
                    isPlatformAdmin: roles.isPlatformAdmin,
                    platformRole: roles.platformRole,
                    organizationRole: roles.organizationRole
                  }
                });
                navigate('/org-admin');
              }} 
              className={`flex flex-col items-center justify-center gap-1 h-14 min-h-[44px] px-3 rounded-lg transition-colors ${
                location.pathname === '/org-admin' 
                  ? 'text-primary bg-primary/10' 
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Building2 className="h-5 w-5" />
              <span className="text-[10px] font-medium">Org</span>
            </Button>
          )}
          
          <Button 
            variant="ghost" 
            onClick={handleProfileClick} 
            className={`flex flex-col items-center justify-center gap-1 h-14 min-h-[44px] px-3 rounded-lg transition-colors ${
              location.pathname === '/profile' 
                ? 'text-primary bg-primary/10' 
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <Avatar className="h-6 w-6">
              <AvatarFallback className={`bg-primary/10 text-xs ${
                location.pathname === '/profile' ? 'bg-primary/20' : ''
              }`}>
                <User className={`h-3 w-3 ${
                  location.pathname === '/profile' ? 'text-primary' : 'text-muted-foreground'
                }`} />
              </AvatarFallback>
            </Avatar>
            <span className="text-[10px] font-medium">Profile</span>
          </Button>
        </div>
      </div>;
  };

  return <>
      <header className="sticky top-0 z-50 w-full bg-background/95 shadow-sm">
        <div className="container flex items-center justify-between h-16 px-4 md:px-8">
          <div className="flex items-center space-x-4">
            <Link to="/app" className="flex items-center gap-2">
              <div className="w-8 h-8">
                <img 
                  src={organizationLogo} 
                  alt={logoAltText} 
                  className="w-full h-full object-contain"
                  style={{ maxWidth: '100%', maxHeight: '100%' }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (target.src !== '/uploads/d4215903-dca2-4d49-9f0f-47cc0cd1fe09.png') {
                      target.src = '/uploads/d4215903-dca2-4d49-9f0f-47cc0cd1fe09.png';
                    }
                  }}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-lg">{organizationName}</span>
                <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 font-semibold bg-primary/10 text-primary border-primary/20">
                  BETA
                </Badge>
              </div>
            </Link>
            
            {/* Organization Switcher */}
            <OrganizationSwitcher />
          </div>
          <div className="flex items-center gap-2">
            {!rolesLoading && isSuperAdmin && <Badge variant="secondary" className="hidden md:flex items-center gap-1 bg-primary/10">
                <ShieldCheck className="h-3 w-3 text-primary" />
                <span className="text-xs">Super Admin</span>
              </Badge>}
            
            {/* Show Bookings button on desktop only */}
            {!isMobile && <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/bookings')} 
                className="gap-2 border hover:bg-muted/50 h-10 px-4"
              >
                <Calendar className="h-4 w-4" />
                <span>Bookings</span>
              </Button>}
            
            {/* Show Admin settings on desktop only */}
            {!isMobile && isSuperAdmin && <Button
                variant="ghost" 
                size="icon" 
                onClick={() => navigate('/admin')} 
                title="Admin Panel" 
                className="border hover:bg-muted/50 h-10 w-10"
              >
                <Settings className="h-4 w-4" />
              </Button>}
            
            {/* Show Organization Admin for org admins on desktop only */}
            {!isMobile && (roles.isOrgAdmin || roles.isPlatformAdmin) && <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate('/org-admin')} 
                title="Organization Admin" 
                className="border hover:bg-muted/50 h-10 w-10"
              >
                <Building2 className="h-4 w-4" />
              </Button>}
            
            <ThemeToggle />
            
            {/* Show profile button on desktop only */}
            {!isMobile && <div className="flex items-center gap-2">
                {!rolesLoading && isAdmin && !isSuperAdmin && <Badge variant="secondary" className="hidden md:flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3" />
                    <span className="text-xs">Admin</span>
                  </Badge>}
                <Button variant="outline" size="icon" onClick={handleProfileClick} className="hover:bg-muted/50 h-10 w-10">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary/10">
                      <User className="h-4 w-4 text-primary" />
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </div>}
          </div>
        </div>
      </header>
      
      {/* Render mobile app bar if on mobile */}
      {isMobile && <MobileAppBar />}
      
      {/* We've removed the duplicate padding div here since we've added it globally in App.tsx */}
    </>;
};

export default Header;
